import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search,
  Refresh,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { EstateAddressOverview as EstateAddressOverviewType } from '../types';

const EstateAddressOverview: React.FC = () => {
  const [addresses, setAddresses] = useState<EstateAddressOverviewType[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [occupiedFilter, setOccupiedFilter] = useState<'all' | 'true' | 'false'>('all');
  const [villageFilter, setVillageFilter] = useState<string>('');
  const [villages, setVillages] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{show: boolean, message: string, type: 'success' | 'error' | 'info'}>({
    show: false,
    message: '',
    type: 'info'
  });

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Statistics state
  const [statistics, setStatistics] = useState<{total: number, occupied: number, available: number}>({
    total: 0,
    occupied: 0,
    available: 0
  });

  // Search debounce
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const navigate = useNavigate();

  // Track component mount state to prevent state updates after unmount
  const isMountedRef = useRef(true);
  // AbortController for API requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0); // Reset to first page when searching
    }, 500); // 500ms delay

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Fetch estate addresses data with pagination and filters
  const fetchAddressesData = useCallback(async () => {
    if (!isMountedRef.current) {
      console.log('[EstateAddressOverview] Component unmounted, skipping fetch');
      return;
    }

    console.log('[EstateAddressOverview] Fetching estate addresses data');

    try {
      setLoading(true);
      setError(null);

      // Cancel any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      // Prepare query parameters for server-side pagination and search
      const params: any = {
        page: page + 1, // Server expects 1-based page numbers
        limit: rowsPerPage,
      };

      if (occupiedFilter !== 'all') {
        params.occupied = occupiedFilter;
      }

      if (villageFilter) {
        params.village = villageFilter;
      }

      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }

      // Make API request with abort signal
      const response = await apiService.getEstateAddressOverview(params, abortControllerRef.current.signal);

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        console.log('[EstateAddressOverview] Got addresses data:', response.data?.length || 0, 'addresses');
        console.log('[EstateAddressOverview] Pagination info:', response.pagination);
        console.log('[EstateAddressOverview] Statistics:', response.statistics);
        setAddresses(response.data || []);
        setTotalCount(response.pagination?.total || 0);
        setVillages(response.villages || []);
        setStatistics(response.statistics || { total: 0, occupied: 0, available: 0 });
        setLoading(false);
      }
    } catch (error: any) {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        // Don't treat aborted requests as errors
        if (error.name === 'CanceledError' || error.name === 'AbortError') {
          console.log('[EstateAddressOverview] Request was aborted');
          return;
        }

        console.error('[EstateAddressOverview] Error fetching data:', error);
        setError('Failed to load estate addresses. Please try again.');
        setLoading(false);
        setNotification({
          show: true,
          message: 'Failed to load estate addresses',
          type: 'error'
        });
      }
    }
  }, [page, rowsPerPage, occupiedFilter, villageFilter, debouncedSearchTerm]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchAddressesData();
  }, [fetchAddressesData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleRefresh = () => {
    setPage(0);
    fetchAddressesData();
  };

  const handleOccupiedFilterChange = (filter: 'all' | 'true' | 'false') => {
    setOccupiedFilter(filter);
    setPage(0); // Reset to first page when filter changes
  };

  const handleVillageFilterChange = (event: any) => {
    setVillageFilter(event.target.value);
    setPage(0); // Reset to first page when filter changes
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (address: EstateAddressOverviewType) => {
    // Only navigate if the address is occupied and has a member
    if (address.occupancy?.member?._id) {
      navigate(`/members/${address.occupancy.member._id}`);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate occupancy percentage
  const occupiedPercentage = statistics.total > 0
    ? Math.round((statistics.occupied / statistics.total) * 100)
    : 0;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Estate Address Overview</Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Paper sx={{ flex: 1, p: 2, bgcolor: '#e8f5e9' }}>
          <Typography variant="h6" color="success.main">
            Occupied
          </Typography>
          <Typography variant="h3" color="success.dark">
            {statistics.occupied}
          </Typography>
        </Paper>
        <Paper sx={{ flex: 1, p: 2, bgcolor: '#f5f5f5' }}>
          <Typography variant="h6" color="textSecondary">
            Available
          </Typography>
          <Typography variant="h3">
            {statistics.available}
          </Typography>
        </Paper>
        <Paper sx={{ flex: 1, p: 2, bgcolor: '#e3f2fd' }}>
          <Typography variant="h6" color="primary">
            Total
          </Typography>
          <Typography variant="h3" color="primary.dark">
            {statistics.total}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {occupiedPercentage}% Occupied
          </Typography>
        </Paper>
      </Box>

      {/* Search Box */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by address or member name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Filter Buttons */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          {/* Occupancy Status Filter */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flex: 1 }}>
            <Typography variant="subtitle1" sx={{ width: '100%', mb: 1 }}>
              Filter by Status:
            </Typography>
            <Button
              variant={occupiedFilter === 'all' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handleOccupiedFilterChange('all')}
            >
              All
            </Button>
            <Button
              variant={occupiedFilter === 'true' ? 'contained' : 'outlined'}
              color="success"
              size="small"
              onClick={() => handleOccupiedFilterChange('true')}
            >
              Occupied
            </Button>
            <Button
              variant={occupiedFilter === 'false' ? 'contained' : 'outlined'}
              color="primary"
              size="small"
              onClick={() => handleOccupiedFilterChange('false')}
            >
              Available
            </Button>
          </Box>

          {/* Village Filter */}
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Filter by Village</InputLabel>
            <Select
              value={villageFilter}
              label="Filter by Village"
              onChange={handleVillageFilterChange}
            >
              <MenuItem value="">
                <em>All Villages</em>
              </MenuItem>
              {villages.map((village) => (
                <MenuItem key={village} value={village}>
                  {village}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          <em>Click on an occupied address row to view member details</em>
        </Typography>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Village Name</TableCell>
              <TableCell>House Number</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Main Member</TableCell>
              <TableCell>Membership Type</TableCell>
              <TableCell>Occupation Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Loading estate addresses...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Alert severity="error">{error}</Alert>
                </TableCell>
              </TableRow>
            ) : addresses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="textSecondary">
                    No estate addresses found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              addresses.map((address) => (
                <TableRow
                  key={address._id}
                  hover={!!address.occupancy}
                  onClick={() => handleRowClick(address)}
                  sx={{
                    cursor: address.occupancy ? 'pointer' : 'default',
                    '&:hover': address.occupancy ? {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    } : {}
                  }}
                >
                  <TableCell>
                    <Typography variant="body1">
                      {address.villageName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1">
                      {address.houseNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={address.isOccupied ? 'Occupied' : 'Available'}
                      color={address.isOccupied ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {address.occupancy ? (
                      <Typography variant="body2">
                        {address.occupancy.member.firstName} {address.occupancy.member.lastName}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {address.occupancy ? (
                      <Chip
                        label={address.occupancy.membershipType}
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {address.occupancy ? formatDate(address.occupancy.occupationDate) : '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      </TableContainer>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.show}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, show: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, show: false })}
          severity={notification.type}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EstateAddressOverview;
